import { EntityEnum } from '@goldcast/api/backend';
import { AlignedRecordings } from '../types/clip-types';
import { isEntityScreenshare, isEntitySharedVideo } from './activityUtils';
import { mergeWindows, subtractRanges } from './rangeUtils';

export const SORT_ORDER_FOR_ENTITIES: {
  [key in EntityEnum]?: number;
} = {
  aligned_screenshare_feed: 0,
  aligned_video_feed: 1,
  aligned_user_feed: 2
}; /**
 * Adjusts the stage time spans of non-shared-video recordings to exclude
 * any time periods where shared video recordings are active.
 *
 * This ensures that no other entities have overlapping stage time spans
 * with shared video content, effectively "subtracting" shared video ranges
 * from other recordings' ranges.
 *
 * @param {AlignedRecordings[]} recordings - The list of aligned recordings. Each recording contains:
 *  - `entity`: Identifier for the entity type.
 *  - `stage_time_spans`: An array of objects with `start_time` and `end_time` (in seconds).
 * @returns {AlignedRecordings[]} A new array of recordings where stage time spans
 * of non-shared-video entities are adjusted to exclude periods covered by shared video.
 *
 * @example
 * const adjusted = adjustStageTimeSpansDueToPresenceOfSharedVideo(recordings);
 * // All non-shared-video stage time spans will no longer overlap with shared video spans.
 */
export const excludeSharedVideoTimeSpans = (recordings: AlignedRecordings[]): AlignedRecordings[] => {
  const sharedVideoRecordings = recordings.filter(recording => isEntitySharedVideo(recording.entity));
  const stageTimeSpansForSharedVideo = sharedVideoRecordings
    .map(
      recording => recording.stage_time_spans?.map(span => [span.start_time, span.end_time] as [number, number]) ?? []
    )
    .flat();
  const mergedSortedTimeSpansForSharedVideo = mergeWindows(stageTimeSpansForSharedVideo);
  return recordings.map(recording => {
    if (isEntitySharedVideo(recording.entity)) {
      return recording;
    }
    const stageTimeSpans = (recording.stage_time_spans?.map(span => [span.start_time, span.end_time]) ?? []) as [
      number,
      number
    ][];
    const mergedAndSortedStageTimeSpans = mergeWindows(stageTimeSpans);
    const adjustedStageTimeSpans = subtractRanges(mergedAndSortedStageTimeSpans, mergedSortedTimeSpansForSharedVideo);
    const finalStageTimeSpans = adjustedStageTimeSpans.map(span => ({ start_time: span[0], end_time: span[1] }));
    return {
      ...recording,
      stage_time_spans: finalStageTimeSpans
    };
  });
};
export const getAlignedRecordingIdFromSpeakerId = (speakerId: string, recordings: AlignedRecordings[]) => {
  const alignedRecording = recordings.find(
    recording =>
      recording.user.id === speakerId &&
      !isEntityScreenshare(recording.entity) &&
      !isEntitySharedVideo(recording.entity)
  );
  return alignedRecording?.id;
};
